// Live Firestore adapter for the shared Brugo project (brugo-core-97965).
import { db } from "@/lib/firebase";
import {
  collection, doc, getDoc, getDocs, addDoc, updateDoc, setDoc, deleteDoc, onSnapshot, writeBatch,
  query, orderBy, limit, startAfter, documentId, getCountFromServer, FieldPath, deleteField, where,
} from "firebase/firestore";

const withId = (snap) => ({ id: snap.id, ...snap.data() });

export const firebaseAdapter = {
  mode: "firebase",
  async list(coll) {
    const snap = await getDocs(collection(db, coll));
    return snap.docs.map(withId);
  },
  async get(coll, id) {
    const snap = await getDoc(doc(db, coll, id));
    return snap.exists() ? withId(snap) : null;
  },
  async add(coll, data) {
    const ref = await addDoc(collection(db, coll), data);
    return { id: ref.id, ...data };
  },
  async update(coll, id, patch) {
    await updateDoc(doc(db, coll, id), patch);
    return this.get(coll, id);
  },
  // Atomically update many docs in one commit (used for lead-level float extend).
  async batchUpdate(coll, updates) {
    const batch = writeBatch(db);
    updates.forEach(({ id, patch }) => batch.update(doc(db, coll, id), patch));
    await batch.commit();
    return updates.length;
  },
  // Atomically update docs across DIFFERENT collections in one commit
  // (used to keep requests + lead_distributions general_phase_ends_at in sync).
  async batchUpdatePaths(items) {
    const batch = writeBatch(db);
    items.forEach(({ coll, id, patch }) => batch.update(doc(db, coll, id), patch));
    await batch.commit();
    return items.length;
  },
  // create-or-merge a document at a fixed id (used for config/app_settings)
  async set(coll, id, data) {
    await setDoc(doc(db, coll, id), data, { merge: true });
    return this.get(coll, id);
  },
  async remove(coll, id) {
    await deleteDoc(doc(db, coll, id));
  },
  // Batched create of many docs (Firestore caps a batch at 500 writes).
  async bulkAdd(coll, items) {
    const CHUNK = 450;
    let created = 0;
    for (let i = 0; i < items.length; i += CHUNK) {
      const batch = writeBatch(db);
      items.slice(i, i + CHUNK).forEach((data) => batch.set(doc(collection(db, coll)), data));
      await batch.commit();
      created += Math.min(CHUNK, items.length - i);
    }
    return created;
  },
  // ---- Data Explorer (path-based; works for subcollections too) ----
  async page(path, { pageSize = 50, after = null } = {}) {
    const parts = [collection(db, path), orderBy(documentId()), limit(pageSize)];
    if (after) parts.splice(2, 0, startAfter(after));
    const snap = await getDocs(query(...parts));
    return { rows: snap.docs.map(withId), last: snap.docs[snap.docs.length - 1] || null, done: snap.docs.length < pageSize };
  },
  async count(path) {
    const s = await getCountFromServer(collection(db, path));
    return s.data().count;
  },
  async patchField(path, id, key, value) {
    await updateDoc(doc(db, path, id), new FieldPath(key), value === undefined ? deleteField() : value);
  },
  async batchPatchField(path, ids, key, value) {
    const CHUNK = 450;
    for (let i = 0; i < ids.length; i += CHUNK) {
      const batch = writeBatch(db);
      ids.slice(i, i + CHUNK).forEach((id) => batch.update(doc(db, path, id), new FieldPath(key), value === undefined ? deleteField() : value));
      await batch.commit();
    }
    return ids.length;
  },
  // Several fields on one doc in a single write. patch: { field: value|undefined(delete) }
  async patchFields(path, id, patch) {
    const args = Object.entries(patch).flatMap(([k, v]) => [new FieldPath(k), v === undefined ? deleteField() : v]);
    await updateDoc(doc(db, path, id), ...args);
  },
  // Several fields on many docs (bulk update), batched 450 docs per commit.
  async batchPatchFields(path, ids, patch) {
    const args = Object.entries(patch).flatMap(([k, v]) => [new FieldPath(k), v === undefined ? deleteField() : v]);
    const CHUNK = 450;
    for (let i = 0; i < ids.length; i += CHUNK) {
      const batch = writeBatch(db);
      ids.slice(i, i + CHUNK).forEach((id) => batch.update(doc(db, path, id), ...args));
      await batch.commit();
    }
    return ids.length;
  },
  async listAll(path) {
    const snap = await getDocs(collection(db, path));
    return snap.docs.map(withId);
  },
  subscribeWhere(path, filters, cb) {
    const q = query(collection(db, path), ...filters.map(([f, op, v]) => where(f, op, v)));
    return onSnapshot(q, (s) => cb(s.docs.map(withId)), (err) => { console.warn(`subscribeWhere(${path})`, err.code); cb([]); });
  },
  async createDoc(path, id, data) {
    if (id) { await setDoc(doc(db, path, id), data); return id; }
    const ref = await addDoc(collection(db, path), data);
    return ref.id;
  },
  subscribe(coll, cb) {
    return onSnapshot(
      collection(db, coll),
      (snap) => cb(snap.docs.map(withId)),
      (err) => { console.warn(`Firestore subscribe(${coll}) error:`, err.code); cb([]); }
    );
  },
  subscribeDoc(coll, id, cb) {
    return onSnapshot(
      doc(db, coll, id),
      (s) => cb(s.exists() ? withId(s) : null),
      (err) => { console.warn(`Firestore subscribeDoc(${coll}/${id}) error:`, err.code); cb(null); }
    );
  },
};
